import React, { useState, useEffect, useCallback } from 'react';
import '../../styles/ExerciseLayout.css';
import QuestionLearnButton from '../full_mock_test/attempt/QuestionLearnButton';

const QuestionRow = ({ question, questionNumber, isExample, answer, handleInputChange, showHint, isReviewMode, isCorrect, isHydrated }) => {
  const cleanQuestionText = question.question_text.replace(/\(Example\)/gi, '').trim();
  const parts = cleanQuestionText.split('{{big_gap}}');
  const labelText = parts[0];
  const postGapText = parts.length > 1 ? parts[1] : null;

  // Split labelText by ':' to align parts separately
  const labelParts = labelText.split(':');
  const hasColon = labelParts.length > 1;
  const leftPart = labelParts[0];
  const rightPart = hasColon ? labelParts[1] : null;

  const isAnswerCorrect = isCorrect === true || isCorrect === 'true';
  const showIncorrectAnswerInReview = isReviewMode && !isAnswerCorrect;

  return (
    <div className="flex flex-col md:flex-row md:items-center gap-3 py-4 border-b border-slate-100 dark:border-slate-700 last:border-0 w-full overflow-hidden">
      <div className="flex items-baseline gap-3 flex-grow min-w-0">
        <span className="w-6 font-bold text-primary-blue text-lg flex-shrink-0 leading-tight">{isExample ? '' : `${questionNumber}.`}</span>
        <label className="flex-1 flex flex-col sm:flex-row sm:justify-between sm:items-baseline font-body font-bold text-slate-600 dark:text-slate-300 pr-2 min-w-0" htmlFor={question.id}>
          <span className="text-left break-words leading-tight py-1">{leftPart}{hasColon ? ':' : ''}</span>
          {rightPart && <span className="text-right opacity-80 sm:ml-2 italic text-sm leading-tight shrink-0 py-1">{rightPart}</span>}
        </label>
      </div>
      <div className="w-full md:w-auto md:min-w-[14rem] lg:min-w-[18rem] flex-shrink-0 flex items-center gap-2">
        {isExample ? (
          <div className="flex-grow px-3 py-2 bg-slate-50 dark:bg-slate-800/50 rounded-xl border-2 border-transparent font-bold text-primary-blue/60 italic text-center">
            {question.accepted_answers[0]}
          </div>
        ) : (
          <div className="flex-grow flex items-center gap-2 min-w-0">
            {showIncorrectAnswerInReview ? (
              <div className="exercise-input-text bg-red-50 px-3 py-1 rounded-lg flex-grow flex items-center min-w-0 h-11">
                {question.user_answer?.answer
                  ? <span className="text-red-500 line-through decoration-2 mr-2 font-bold truncate max-w-[80px] sm:max-w-none">{question.user_answer.answer}</span>
                  : <span className="text-gray-400 mr-2 font-bold">__</span>
                }
                <span className="text-green-600 font-bold shrink-0">{question.accepted_answers[0]}</span>
              </div>
            ) : (
              <input
                type="text"
                id={question.id}
                autoComplete="off"
                value={showHint || (isReviewMode && isAnswerCorrect) ? question.accepted_answers[0] : (answer || '')}
                onChange={(e) => handleInputChange(question.id, e.target.value)}
                className={`flex-grow bg-slate-50 dark:bg-slate-800 border-2 border-slate-200 dark:border-slate-600 focus:ring-4 focus:ring-blue-100 focus:border-primary-blue rounded-xl px-4 py-2 font-bold text-primary-blue dark:text-blue-400 transition-all outline-none min-w-0 h-11 ${showHint ? 'hint-input' : ''}`}
                readOnly={showHint || !isHydrated}
                disabled={isReviewMode || !isHydrated}
              />
            )}
            {isReviewMode && (
              <div className="shrink-0 flex items-center gap-2">
                {(isCorrect === true || isCorrect === 'true')
                  ? <span className="correct-checkmark">✓</span>
                  : <span className="incorrect-checkmark">✗</span>
                }
                {!isExample && <QuestionLearnButton question={question} />}
              </div>
            )}
          </div>
        )}
        {postGapText && <span className="font-body font-bold text-slate-600 dark:text-slate-400 shrink-0">{postGapText}</span>}
      </div>
    </div>
  );
};

const ListenPart2 = ({ exerciseData, attemptId, exerciseId, showHint, isReviewMode, fillHintSignal }) => {
  const [answers, setAnswers] = useState({});
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    if (attemptId && exerciseId) {
      // 1. Prioritize API data in review mode
      if (isReviewMode && exerciseData.questions) {
        const migratedAnswers = {};
        exerciseData.questions.forEach(q => {
          if (q.user_answer && typeof q.user_answer.answer === 'string') {
            migratedAnswers[q.id] = q.user_answer.answer;
          }
        });
        if (Object.keys(migratedAnswers).length > 0) {
          setAnswers(migratedAnswers);
          setIsHydrated(true);
          return;
        }
      }

      // 2. Otherwise load from localStorage
      const attemptKey = `attempt_${attemptId}`;
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) {
        try {
          const allAnswers = JSON.parse(savedAttempt);
          const savedData = allAnswers[exerciseId];
          if (savedData && typeof savedData === 'object') {
            const migratedAnswers = {};
            Object.keys(savedData).forEach(qId => {
              const value = savedData[qId];
              migratedAnswers[qId] = (typeof value === 'string') ? value : value.answer;
            });
            setAnswers(migratedAnswers);
          }
        } catch (e) {
          console.error("Failed to parse saved attempt from localStorage", e);
        }
      }
      setIsHydrated(true);
    }
  }, [attemptId, exerciseId, isReviewMode]);

  const saveAnswersToLocalStorage = useCallback((currentAnswers) => {
    if (!isHydrated || !attemptId || !exerciseId) return;

    const attemptKey = `attempt_${attemptId}`;
    let allAnswers = {};
    try {
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) allAnswers = JSON.parse(savedAttempt) || {};
    } catch (e) {
      allAnswers = {};
    }

    const answersToSave = {};
    Object.keys(currentAnswers).forEach((qId) => {
      answersToSave[qId] = { answer: currentAnswers[qId] };
    });

    allAnswers[exerciseId] = answersToSave;
    localStorage.setItem(attemptKey, JSON.stringify(allAnswers));
  }, [attemptId, exerciseId, isHydrated]);


  useEffect(() => {
    if (!isHydrated) {
      return;
    }

    const debounceSave = setTimeout(() => {
      saveAnswersToLocalStorage(answers);
    }, 500);

    return () => {
      clearTimeout(debounceSave);
      saveAnswersToLocalStorage(answers);
    };
  }, [answers, isHydrated, saveAnswersToLocalStorage]);

  useEffect(() => {
    if (fillHintSignal > 0) {
      const newAnswers = {};
      exerciseData.questions.forEach(q => {
        if (q.accepted_answers && q.accepted_answers.length > 0) {
          newAnswers[q.id] = q.accepted_answers[0];
        }
      });
      setAnswers(newAnswers);
      saveAnswersToLocalStorage(newAnswers);
    }
  }, [fillHintSignal, exerciseData.questions, saveAnswersToLocalStorage]);

  if (!exerciseData || !exerciseData.content_data || !exerciseData.questions) {
    return <div>Loading exercise data...</div>;
  }

  const {
    image_url,
    story_title,
  } = exerciseData.content_data;

  const { questions } = exerciseData;
  const exampleQuestion = questions.find(q => q.order_index === 0);
  const mainQuestions = questions.filter(q => q.order_index > 0).sort((a, b) => a.order_index - b.order_index);
  const ASSET_BASE_URL = process.env.REACT_APP_MEDIA_PREFIX || 'http://localhost:8000';
  const resolveUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    return `${ASSET_BASE_URL}${url}`;
  };

  const handleInputChange = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  return (
    <div className="w-full h-full p-1 md:px-10">
      <div className="relative flex flex-col h-full bg-transparent dark:from-slate-800/50 dark:to-card-dark rounded-2xl p-2 md:p-6 overflow-y-auto custom-scrollbar">
        {image_url && (
          <div className="flex justify-center mb-4">
            <img
              src={resolveUrl(image_url)}
              alt="Exercise illustration"
              className="rounded-xl max-w-full max-h-52 object-contain shadow-sm border border-gray-100 p-1 bg-white"
            />
          </div>
        )}
        <div className="max-w-3xl mx-auto w-full bg-white dark:bg-slate-800/50 rounded-2xl border-2 border-gray-200 dark:border-slate-600 p-3 md:p-8 text-center shadow-sm">
          {story_title && <h2 className="exercise-story-title">{story_title}</h2>}
          <div className="space-y-1 text-left">
            {exampleQuestion &&
              <QuestionRow
                question={exampleQuestion}
                isExample={true}
              />}
            {mainQuestions.map((q, index) => (
              <QuestionRow
                key={q.id}
                question={q}
                questionNumber={index + 1}
                isExample={false}
                answer={answers[q.id]}
                handleInputChange={handleInputChange}
                showHint={showHint}
                isReviewMode={isReviewMode}
                isCorrect={q.is_correct}
                isHydrated={isHydrated}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListenPart2;